import { useState, useEffect }      from 'react';
import { Crown, Tv }               from 'lucide-react';
import type { FutebolVotacaoJogo }  from '@/shared/types';
import { useVotacaoCandidatos }     from '../hooks/useVotacaoCandidatos';

function Escudo({ src, nome }: { src: string | null; nome: string }) {
  const [erro, setErro] = useState(false);
  if (!src || erro) {
    return (
      <span className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-[10px] font-bold text-white/60 shrink-0">
        {nome.slice(0, 2).toUpperCase()}
      </span>
    );
  }
  return (
    <img src={src} alt={nome} className="w-10 h-10 object-contain shrink-0" onError={() => setErro(true)} />
  );
}

// ── Anúncio do vencedor ───────────────────────────────────────────────────────

const DURACAO_MOBILE_MS = 10_000;

const CONFETES_MOBILE = [
  { left:'10%', top:'6%',  delay:'0s',    color:'#fbbf24' },
  { left:'25%', top:'3%',  delay:'0.1s',  color:'#34d399' },
  { left:'45%', top:'2%',  delay:'0.05s', color:'#f87171' },
  { left:'62%', top:'5%',  delay:'0.2s',  color:'#60a5fa' },
  { left:'78%', top:'3%',  delay:'0.15s', color:'#fbbf24' },
  { left:'88%', top:'8%',  delay:'0.25s', color:'#a78bfa' },
];

function AnuncioVencedor({ jogo }: { jogo: FutebolVotacaoJogo }) {
  const [visivel, setVisivel] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setVisivel(false), DURACAO_MOBILE_MS);
    return () => clearTimeout(t);
  }, [jogo.id]);

  if (!visivel) return null;

  return (
    <div
      className="animate-vm-entrada relative rounded-2xl overflow-hidden"
      style={{ border: '1.5px solid rgba(251,191,36,0.45)', background: 'linear-gradient(160deg, #0d0b04 0%, #130f02 100%)' }}
    >
      {/* Confetes */}
      {CONFETES_MOBILE.map((c, i) => (
        <span
          key={i}
          className="animate-vm-confete absolute w-1.5 h-1.5 rounded-[1px]"
          style={{ left: c.left, top: c.top, animationDelay: c.delay, background: c.color }}
        />
      ))}

      {/* Cabeçalho */}
      <div className="flex items-center justify-center gap-2 px-4 pt-4 pb-2">
        <span className="animate-vm-coroa inline-block">
          <Crown size={24} className="text-amber-400" fill="currentColor" strokeWidth={0} />
        </span>
        <div className="text-center">
          <p className="text-xs font-black text-amber-400 uppercase tracking-widest leading-tight">Escolha do Salão!</p>
          <p className="text-[10px] text-amber-400/50 mt-0.5">O salão decidiu</p>
        </div>
        <span className="animate-vm-coroa inline-block" style={{ animationDelay: '0.65s' }}>
          <Crown size={24} className="text-amber-400" fill="currentColor" strokeWidth={0} />
        </span>
      </div>

      {/* Times */}
      <div className="flex items-center gap-3 px-5 pb-3">
        <div className="flex flex-col items-center gap-1.5 flex-1 min-w-0">
          <Escudo src={jogo.escudo_casa} nome={jogo.time_casa} />
          <span className="text-[11px] font-bold text-white/90 text-center leading-tight w-full truncate">{jogo.time_casa}</span>
        </div>
        <span className="text-white/20 font-black text-base shrink-0">×</span>
        <div className="flex flex-col items-center gap-1.5 flex-1 min-w-0">
          <Escudo src={jogo.escudo_visitante} nome={jogo.time_visitante} />
          <span className="text-[11px] font-bold text-white/90 text-center leading-tight w-full truncate">{jogo.time_visitante}</span>
        </div>
      </div>

      {/* Confirmação */}
      <div className="mx-4 mb-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-center">
        <p className="text-xs font-bold text-emerald-400">✓ Esse jogo vai passar na TV do salão!</p>
        <p className="text-[10px] text-white/25 mt-0.5">{jogo.votos} {jogo.votos === 1 ? 'voto' : 'votos'}</p>
      </div>

      {/* Barra auto-hide */}
      <div className="h-0.5 bg-white/5">
        <div
          className="animate-vm-bar h-full bg-amber-400/50 rounded-full"
          style={{ '--vm-bar-dur': `${DURACAO_MOBILE_MS}ms` } as React.CSSProperties}
        />
      </div>
    </div>
  );
}

// ── Card de votação / placar parcial ─────────────────────────────────────────

const CORES_BARRA = ['bg-amber-400', 'bg-sky-400', 'bg-emerald-400'] as const;

function CardJogo({ jogo, votando, onVotar, totalVotos, votado, rank, isLider }: {
  jogo: FutebolVotacaoJogo;
  votando: boolean;
  onVotar?: (id: number) => void;
  totalVotos: number;
  votado: number | null;
  rank: number;
  isLider: boolean;
}) {
  const pct      = totalVotos > 0 ? Math.round((jogo.votos / totalVotos) * 100) : 0;
  const isMeu    = votado === jogo.fixture_id;
  const corBarra = CORES_BARRA[rank] ?? 'bg-emerald-400';
  const modoPlacar = votado !== null;

  return (
    <div className={`px-4 py-4 border-b border-white/[0.05] last:border-0 ${isMeu ? 'bg-emerald-500/[0.04]' : ''}`}>
      {jogo.liga_nome && (
        <p className="text-[9px] font-bold text-emerald-400/70 uppercase tracking-widest mb-2.5">{jogo.liga_nome}</p>
      )}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex flex-col items-center gap-1.5 flex-1 min-w-0">
          <Escudo src={jogo.escudo_casa} nome={jogo.time_casa} />
          <span className="text-[11px] font-semibold text-white/90 text-center leading-tight w-full truncate">{jogo.time_casa}</span>
        </div>
        <span className="text-white/20 font-black text-base shrink-0 pb-4">×</span>
        <div className="flex flex-col items-center gap-1.5 flex-1 min-w-0">
          <Escudo src={jogo.escudo_visitante} nome={jogo.time_visitante} />
          <span className="text-[11px] font-semibold text-white/90 text-center leading-tight w-full truncate">{jogo.time_visitante}</span>
        </div>
      </div>

      {modoPlacar ? (
        <>
          <div className="relative w-full h-2 bg-white/[0.08] rounded-full overflow-hidden mb-1.5">
            <div
              className={`h-full rounded-full transition-all duration-700 ease-out ${corBarra}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-[11px]">
            <span className={`font-bold ${isLider ? 'text-amber-400' : 'text-white/55'}`}>
              {isLider ? '👑 ' : ''}{pct}%
            </span>
            <span className="text-white/50">
              {jogo.votos} {jogo.votos === 1 ? 'voto' : 'votos'}
              {isMeu ? ' · seu voto' : ''}
            </span>
          </div>
        </>
      ) : (
        <button
          onClick={() => onVotar?.(jogo.fixture_id)}
          disabled={votando}
          className="w-full py-2.5 rounded-xl text-xs font-bold text-white bg-emerald-700 hover:bg-emerald-600 active:scale-[0.98] transition-all disabled:opacity-40 min-h-11"
        >
          Votar nesse jogo
        </button>
      )}
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

interface MobilePreGameVotingProps {
  telaId: string;
  isHT?: boolean;
}

export default function MobilePreGameVoting({ telaId, isHT = false }: MobilePreGameVotingProps) {
  const {
    jogos, loading, votado, votando, vencedor, fixtureIdAtivo,
    totalVotos, lider, votacaoAberta, votar,
  } = useVotacaoCandidatos(telaId);

  if (loading || isHT) return null;

  if (vencedor && fixtureIdAtivo !== vencedor.fixture_id) return <AnuncioVencedor jogo={vencedor} />;

  if (jogos.length === 0) return null;

  // Único jogo na janela — informa que será transmitido, sem pedir voto
  if (jogos.length === 1) {
    const jogo = jogos[0];
    return (
      <div className="rounded-2xl overflow-hidden ring-1 ring-sky-500/20">
        <div className="bg-gradient-to-r from-sky-950/80 to-blue-950/60 px-4 py-3 border-b border-sky-500/10 flex items-center gap-2.5">
          <Tv size={18} className="text-sky-400/60 shrink-0" />
          <div>
            <p className="text-xs font-bold text-white leading-tight">Em breve na TV do salão</p>
            <p className="text-[10px] text-sky-400/60 mt-0.5">Único jogo disponível neste horário</p>
          </div>
        </div>
        <div className="bg-white/[0.02] px-4 py-4">
          {jogo.liga_nome && (
            <p className="text-[9px] font-bold text-sky-400/70 uppercase tracking-widest mb-2.5">{jogo.liga_nome}</p>
          )}
          <div className="flex items-center gap-2 mb-3">
            <div className="flex flex-col items-center gap-1.5 flex-1 min-w-0">
              <Escudo src={jogo.escudo_casa} nome={jogo.time_casa} />
              <span className="text-[11px] font-semibold text-white/90 text-center leading-tight w-full truncate">{jogo.time_casa}</span>
            </div>
            <span className="text-white/20 font-black text-base shrink-0">×</span>
            <div className="flex flex-col items-center gap-1.5 flex-1 min-w-0">
              <Escudo src={jogo.escudo_visitante} nome={jogo.time_visitante} />
              <span className="text-[11px] font-semibold text-white/90 text-center leading-tight w-full truncate">{jogo.time_visitante}</span>
            </div>
          </div>
          <div className="py-2 rounded-xl bg-sky-500/10 border border-sky-500/20 text-center">
            <p className="text-xs font-bold text-sky-400">✓ Transmissão confirmada</p>
          </div>
        </div>
      </div>
    );
  }

  // Usuário já votou — placar parcial em tempo real até o ciclo fechar
  if (votado !== null && votacaoAberta) {
    return (
      <div className="rounded-2xl overflow-hidden ring-1 ring-emerald-500/20">
        <div className="bg-gradient-to-r from-emerald-950/80 to-green-950/60 px-4 py-3 border-b border-emerald-500/10">
          <p className="text-xs font-bold text-emerald-400 leading-tight">Voto registrado!</p>
          <p className="text-[10px] text-white/50 mt-0.5">
            Placar parcial · {totalVotos} {totalVotos === 1 ? 'voto' : 'votos'} no salão
          </p>
        </div>
        <div className="bg-white/[0.02]">
          {jogos.map((jogo, i) => (
            <CardJogo
              key={jogo.id}
              jogo={jogo}
              votando={votando}
              totalVotos={totalVotos}
              votado={votado}
              rank={i}
              isLider={lider?.id === jogo.id}
            />
          ))}
        </div>
      </div>
    );
  }

  if (!votacaoAberta) return null;

  return (
    <div className="rounded-2xl overflow-hidden ring-1 ring-emerald-500/20">
      <div className="bg-gradient-to-r from-emerald-950/80 to-green-950/60 px-4 py-3 border-b border-emerald-500/10 flex items-center gap-2.5">
        <span className="text-lg leading-none" aria-hidden>⚽</span>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold text-white leading-tight">Qual jogo quer assistir?</p>
          <p className="text-[10px] text-emerald-400/60 mt-0.5">
            Vote no jogo que vai passar na TV do salão
            {fixtureIdAtivo !== null ? ' · próximo horário' : ''}
          </p>
        </div>
      </div>
      <div className="bg-white/[0.02]">
        {jogos.map((jogo, i) => (
          <CardJogo
            key={jogo.id}
            jogo={jogo}
            votando={votando}
            onVotar={votar}
            totalVotos={totalVotos}
            votado={votado}
            rank={i}
            isLider={lider?.id === jogo.id}
          />
        ))}
      </div>
      {totalVotos > 0 && (
        <p className="text-center text-[10px] text-white/40 py-2 border-t border-white/[0.05]">
          {totalVotos} {totalVotos === 1 ? 'voto' : 'votos'} registrados até agora
        </p>
      )}
    </div>
  );
}
